import { useNavigate } from 'react-router-dom';
import { usePlayer } from '@/lib/PlayerContext';
import { formatDuration, formatRelativeDate } from '@/utils/format';
import { ActionButtons } from './ActionButtons';

/** Episode data for the carousel card - uses camelCase (GraphQL convention) */
export interface CarouselEpisode {
  topicSlug: string;
  topicName: string;
  episodeId: string;
  title: string;
  summary: string;
  audioS3Key: string;
  durationSeconds: number;
  publishedAt: string;
}

interface CarouselEpisodeCardProps {
  episode: CarouselEpisode;
  isAuthenticated?: boolean;
  isLiked?: boolean;
  isPending?: boolean;
  onToggleLike?: () => void;
}

/**
 * Compact episode card for use in the carousel.
 * Displays podcast badge, episode title (2 lines), summary (truncated),
 * duration, and publish date with glassmorphism styling.
 */
export function CarouselEpisodeCard({
  episode,
  isAuthenticated = false,
  isLiked = false,
  isPending = false,
  onToggleLike,
}: CarouselEpisodeCardProps) {
  const navigate = useNavigate();
  const { playEpisode, currentEpisode, isPlaying, setIsPlaying } = usePlayer();

  const isCurrentEpisode = currentEpisode?.episodeId === episode.episodeId;
  const isCurrentlyPlaying = isCurrentEpisode && isPlaying;

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCurrentEpisode) {
      setIsPlaying(!isPlaying);
    } else {
      playEpisode({
        episodeId: episode.episodeId,
        topicSlug: episode.topicSlug,
        topicName: episode.topicName,
        title: episode.title,
        summary: episode.summary,
        audioS3Key: episode.audioS3Key,
        durationSeconds: episode.durationSeconds,
        publishedAt: episode.publishedAt,
      });
    }
  };

  const handleCardClick = () => {
    navigate(`/p/${episode.topicSlug}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <div
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      // biome-ignore lint/a11y/useSemanticElements: Card with nested play button
      role="button"
      tabIndex={0}
      className={`group relative h-full overflow-hidden rounded-xl border bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/5 cursor-pointer ${
        isCurrentEpisode
          ? 'border-indigo-500 ring-1 ring-indigo-500/50'
          : 'border-gray-800 hover:border-gray-700'
      }`}
    >
      {/* Hover gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/5 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />

      <div className="relative flex h-full flex-col">
        {/* Top row: Podcast badge and action buttons */}
        <div className="mb-3 flex items-start justify-between">
          <div className="inline-flex items-center gap-1.5">
            <div className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-indigo-500/30 to-purple-500/30 ring-1 ring-white/10">
              <svg
                className="h-3 w-3 text-indigo-400"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 3C7.03 3 3 7.03 3 12v7c0 1.1.9 2 2 2h2v-8H5v-1c0-3.87 3.13-7 7-7s7 3.13 7 7v1h-2v8h2c1.1 0 2-.9 2-2v-7c0-4.97-4.03-9-9-9zM7 15v4H5v-4h2zm12 4h-2v-4h2v4z" />
              </svg>
            </div>
            <span className="truncate text-xs font-medium text-indigo-300">
              {episode.topicName}
            </span>
          </div>
          <ActionButtons
            isFavorited={isLiked}
            isPending={isPending}
            onToggleFavorite={onToggleLike ?? (() => {})}
            shareUrl={`${window.location.origin}/p/${episode.topicSlug}?ep=${episode.episodeId}`}
            shareTitle={`${episode.title} - ${episode.topicName}`}
            showFavorite={isAuthenticated}
            size="sm"
          />
        </div>

        {/* Episode title - 2 lines max */}
        <h3 className="mb-2 line-clamp-2 text-sm font-semibold text-white transition-colors group-hover:text-indigo-200">
          {episode.title}
        </h3>

        {/* Summary - truncated */}
        <p className="mb-3 line-clamp-2 flex-1 text-xs leading-relaxed text-gray-400">
          {episode.summary}
        </p>

        {/* Bottom row: metadata and play button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{formatDuration(episode.durationSeconds)}</span>
            <span className="text-gray-600">·</span>
            <span>{formatRelativeDate(episode.publishedAt)}</span>
          </div>

          {/* Play button */}
          <button
            type="button"
            onClick={handlePlayClick}
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all ${
              isCurrentlyPlaying
                ? 'bg-indigo-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-indigo-500 hover:text-white'
            }`}
            title={isCurrentlyPlaying ? 'Pause' : 'Play'}
            aria-label={isCurrentlyPlaying ? `Pause ${episode.title}` : `Play ${episode.title}`}
          >
            {isCurrentlyPlaying ? (
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
